/* Smart Size Suggestion modal
 * Custom element that calculates a recommended size from height + weight + optional measurements,
 * persists to localStorage, and hands off to the variant picker.
 */
(function () {
  if (typeof window === 'undefined') return;
  if (window.customElements && window.customElements.get('smart-size-suggestion')) return;

  var LS_KEY = 'mj:smart-size:v1';
  var LS_RESULT_PREFIX = 'mj:smart-size-result:';
  var LS_LAST_KEY = 'mj:smart-size-last';

  function safeGet(key) {
    try { return window.localStorage.getItem(key); } catch (e) { return null; }
  }
  function safeSet(key, val) {
    try { window.localStorage.setItem(key, val); } catch (e) {}
  }
  function safeRemove(key) {
    try { window.localStorage.removeItem(key); } catch (e) {}
  }

  function parseDecimal(str) {
    if (str == null) return NaN;
    return parseFloat(String(str).replace(',', '.').trim());
  }

  function cmFromIn(v) { return v * 2.54; }
  function inFromCm(v) { return v / 2.54; }
  function kgFromLb(v) { return v * 0.45359237; }
  function lbFromKg(v) { return v / 0.45359237; }

  function toCm(v, unit) { return unit === 'in' ? cmFromIn(v) : v; }
  function toKg(v, unit) { return unit === 'lb' ? kgFromLb(v) : v; }

  function distance(h, w, row, hCenter, wCenter) {
    // L2 distance scaled by range widths
    var dh = (h - hCenter) / Math.max(row.hMax - row.hMin, 1);
    var dw = (w - wCenter) / Math.max(row.wMax - row.wMin, 1);
    return Math.sqrt(dh * dh + dw * dw);
  }

  function findBaseSize(matrix, heightCm, weightKg) {
    if (!matrix || !matrix.length) return { size: null, confidence: 'none', candidates: [] };
    var matches = matrix.filter(function (r) {
      return heightCm >= r.hMin && heightCm <= r.hMax && weightKg >= r.wMin && weightKg <= r.wMax;
    });
    if (matches.length === 1) return { size: matches[0].size, confidence: 'high', candidates: [matches[0]] };
    if (matches.length > 1) {
      // Choose midmost candidate (closest to range centre)
      var scored = matches.map(function (r) {
        var hC = (r.hMin + r.hMax) / 2;
        var wC = (r.wMin + r.wMax) / 2;
        return { row: r, d: distance(heightCm, weightKg, r, hC, wC) };
      }).sort(function (a, b) { return a.d - b.d; });
      return { size: scored[0].row.size, confidence: 'medium', candidates: matches };
    }
    // No exact match — nearest by distance to centroid
    var nearest = matrix.map(function (r) {
      var hC = (r.hMin + r.hMax) / 2;
      var wC = (r.wMin + r.wMax) / 2;
      return { row: r, d: distance(heightCm, weightKg, r, hC, wC) };
    }).sort(function (a, b) { return a.d - b.d; });
    return { size: nearest[0].row.size, confidence: 'low', candidates: [nearest[0].row, nearest[1] ? nearest[1].row : null].filter(Boolean) };
  }

  function tallyOptionalVotes(optionalRanges, optionalValues) {
    var votes = {};
    if (!optionalRanges || !optionalRanges.length) return votes;
    var ids = Object.keys(optionalValues);
    for (var i = 0; i < ids.length; i++) {
      var id = ids[i];
      var v = optionalValues[id];
      if (!isFinite(v)) continue;
      for (var j = 0; j < optionalRanges.length; j++) {
        var r = optionalRanges[j];
        if (r.id !== id) continue;
        if (v >= r.min && v <= r.max) {
          votes[r.size] = (votes[r.size] || 0) + 1;
        }
      }
    }
    return votes;
  }

  function applyBodyTypeVote(votes, matrix, base, bodyType) {
    // narrow → adds a vote for the next-smaller size
    // large  → adds a vote for the next-larger size
    // average → no vote
    if (!bodyType || bodyType === 'average') return votes;
    if (!matrix || !matrix.length) return votes;
    var idx = -1;
    for (var i = 0; i < matrix.length; i++) {
      if (matrix[i].size === base) { idx = i; break; }
    }
    if (idx < 0) return votes;
    var targetIdx = bodyType === 'narrow' ? idx - 1 : idx + 1;
    if (targetIdx < 0 || targetIdx >= matrix.length) return votes;
    var targetSize = matrix[targetIdx].size;
    votes[targetSize] = (votes[targetSize] || 0) + 1;
    return votes;
  }

  function reconcile(base, secondary, votes, matrix, bodyType) {
    var keys = Object.keys(votes);
    if (!keys.length) return { size: base, secondary: secondary, explanationKey: 'base' };
    var top = keys.reduce(function (acc, k) { return votes[k] > votes[acc] ? k : acc; }, keys[0]);
    var topCount = votes[top];

    if (top === base) {
      return { size: base, secondary: secondary, explanationKey: 'reinforced' };
    }
    // Body type alone (1 vote) is enough to flip if it points to an adjacent size and there are no contradicting optional votes
    var bodyTypeTriggered = (bodyType === 'narrow' || bodyType === 'large');
    var requiredVotes = bodyTypeTriggered && topCount === 1 ? 1 : 2;

    if (topCount >= requiredVotes) {
      var baseIdx = -1, topIdx = -1;
      for (var i = 0; i < matrix.length; i++) {
        if (matrix[i].size === base) baseIdx = i;
        if (matrix[i].size === top) topIdx = i;
      }
      if (baseIdx >= 0 && topIdx >= 0 && Math.abs(baseIdx - topIdx) <= 1) {
        var key = topCount === 1 && bodyTypeTriggered ? 'bodyTypeOverride' : 'optionalOverride';
        return { size: top, secondary: base, explanationKey: key };
      }
    }
    return { size: base, secondary: top, explanationKey: 'mixed' };
  }

  function buildExplanation(template, size, key) {
    var t = template || 'Based on your height and weight, you’ll likely fit [ {size} ].';
    var suffixMap = {
      base: '',
      reinforced: ' Your other measurements agree.',
      optionalOverride: ' Your body measurements point to a slightly different size.',
      bodyTypeOverride: ' Your body type suggests this for a better fit.',
      mixed: ' Your measurements are mixed — go with this for a balanced fit.'
    };
    return t.replace('{size}', size) + (suffixMap[key] || '');
  }

  /* ───────────────── Custom Element ───────────────── */
  function SmartSizeSuggestion() { return Reflect.construct(HTMLElement, [], SmartSizeSuggestion); }
  SmartSizeSuggestion.prototype = Object.create(HTMLElement.prototype);
  Object.setPrototypeOf(SmartSizeSuggestion, HTMLElement);

  SmartSizeSuggestion.prototype.connectedCallback = function () {
    if (this._initialized) return;
    this._initialized = true;

    var configEl = this.querySelector('[data-smart-size-config]');
    try {
      this.config = configEl ? JSON.parse(configEl.textContent) : {};
    } catch (e) {
      this.config = {};
    }

    this.form = this.querySelector('[data-form]');
    this.panel = this.querySelector('.smart-size-modal__panel');
    this.formPane = this.querySelector('[data-pane="form"]');
    this.resultPane = this.querySelector('[data-pane="result"]');
    this.resultSizeEl = this.querySelector('[data-result-size]');
    this.resultExplanationEl = this.querySelector('[data-result-explanation]');
    this.secondaryWrap = this.querySelector('[data-secondary-wrap]');
    this.secondarySizeEl = this.querySelector('[data-secondary-size]');
    this.lastBanner = this.querySelector('[data-last-banner]');
    this.lastSizeEl = this.querySelector('[data-last-size]');
    this.applyNote = this.querySelector('[data-apply-note]');
    this.applyNoteText = this.querySelector('[data-apply-note-text]');
    this.remindBtn = this.querySelector('[data-action="remind"]');
    this.heading = this.querySelector('[id^="smart-size-result-heading"]');

    this.units = {
      length: this.config.defaultUnits && this.config.defaultUnits.length || 'cm',
      weight: this.config.defaultUnits && this.config.defaultUnits.weight || 'kg'
    };

    this.bindEvents();
    this.hydrateFromStorage();
    this.refreshLastBanner();
  };

  SmartSizeSuggestion.prototype.bindEvents = function () {
    var self = this;

    // Close handlers
    this.addEventListener('click', function (e) {
      var t = e.target.closest('[data-close]');
      if (t) { e.preventDefault(); self.close(); return; }
      var action = e.target.closest('[data-action]');
      if (action) {
        var a = action.getAttribute('data-action');
        if (a === 'reset') { self.resetForm(); }
        else if (a === 'edit') { self.showPane('form'); }
        else if (a === 'apply') {
          if (self._applyMode === 'remind') self.remindSize();
          else self.applySize();
        }
        else if (a === 'remind') { self.remindSize(); }
      }
      var useLast = e.target.closest('[data-use-last]');
      if (useLast) { self.useLastResult(); }
      var useBought = e.target.closest('[data-use-bought]');
      if (useBought) { self.useBoughtSize(useBought); }
    });

    // Escape key
    this._escHandler = function (e) {
      if (e.key === 'Escape' && !self.hidden) {
        e.preventDefault();
        self.close();
      }
    };
    document.addEventListener('keydown', this._escHandler);

    // Unit toggles
    this.querySelectorAll('[data-unit-group]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var group = btn.getAttribute('data-unit-group');
        var newUnit = btn.getAttribute('data-unit-value');
        if (self.units[group] === newUnit) return;
        var oldUnit = self.units[group];
        self.units[group] = newUnit;
        // Update active state
        self.querySelectorAll('[data-unit-group="' + group + '"]').forEach(function (b) {
          var on = b.getAttribute('data-unit-value') === newUnit;
          b.classList.toggle('is-active', on);
          b.setAttribute('aria-pressed', on ? 'true' : 'false');
        });

        // Height has a special imperial layout (ft + in vs single cm)
        if (group === 'length') {
          self.switchHeightMode(oldUnit, newUnit);
        }

        // Convert weight inputs only (height is handled by switchHeightMode above)
        if (group === 'weight') {
          self.querySelectorAll('[data-unit-target="weight"]').forEach(function (inp) {
            var current = parseDecimal(inp.value);
            if (!isFinite(current)) return;
            var converted;
            if (oldUnit === 'kg' && newUnit === 'lb') converted = lbFromKg(current);
            else if (oldUnit === 'lb' && newUnit === 'kg') converted = kgFromLb(current);
            else converted = current;
            inp.value = Math.round(converted * 10) / 10;
          });
        }
      });
    });

    // Form submit
    this.form.addEventListener('submit', function (e) {
      e.preventDefault();
      self.compute();
    });

    // Reset event from native form reset
    this.form.addEventListener('reset', function () {
      setTimeout(function () { self.clearErrors(); }, 0);
    });

    // Global trigger delegation — only attach once
    if (!window.__smartSizeTriggerBound) {
      window.__smartSizeTriggerBound = true;
      document.addEventListener('click', function (e) {
        var trigger = e.target.closest('[data-smart-size-trigger]');
        if (!trigger) return;
        e.preventDefault();
        var targetId = trigger.getAttribute('data-target');
        var target = targetId ? document.querySelector('smart-size-suggestion[data-section-id="' + targetId + '"]')
                              : document.querySelector('smart-size-suggestion');
        if (target && typeof target.open === 'function') {
          target.lastTriggerEl = trigger;
          target.open();
        }
      });
    }
  };

  SmartSizeSuggestion.prototype.open = function () {
    this.hidden = false;
    document.body.classList.add('smart-size-modal-open');
    // Always refresh from storage so we don't show a stale cached size
    this.refreshLastBanner();
    // If there's a result already shown (from a previous compute or cache), open to result pane
    if (this._lastResult && this._lastResult.size) {
      this.showPane('result');
    } else if (this._cachedLastSize) {
      this.useLastResult();
    } else {
      this.showPane('form');
    }
    var self = this;
    requestAnimationFrame(function () {
      if (self.panel) self.panel.focus();
    });
    this.attachFocusTrap();
  };

  SmartSizeSuggestion.prototype.close = function () {
    this.hidden = true;
    document.body.classList.remove('smart-size-modal-open');
    this.detachFocusTrap();
    if (this.lastTriggerEl && typeof this.lastTriggerEl.focus === 'function') {
      this.lastTriggerEl.focus();
    }
  };

  SmartSizeSuggestion.prototype.attachFocusTrap = function () {
    var self = this;
    this._trapHandler = function (e) {
      if (e.key !== 'Tab' || self.hidden) return;
      var focusables = self.querySelectorAll(
        'button:not([disabled]), [href], input:not([disabled]), select, textarea, summary, [tabindex]:not([tabindex="-1"])'
      );
      if (!focusables.length) return;
      var first = focusables[0];
      var last = focusables[focusables.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };
    this.addEventListener('keydown', this._trapHandler);
  };
  SmartSizeSuggestion.prototype.detachFocusTrap = function () {
    if (this._trapHandler) this.removeEventListener('keydown', this._trapHandler);
  };

  SmartSizeSuggestion.prototype.readHeightCm = function () {
    if (this.units.length === 'imperial') {
      var ftInp = this.querySelector('[data-input="height_ft"]');
      var inInp = this.querySelector('[data-input="height_in"]');
      var ft = parseDecimal(ftInp ? ftInp.value : '');
      var inc = parseDecimal(inInp ? inInp.value : '');
      if (!isFinite(ft)) ft = 0;
      if (!isFinite(inc)) inc = 0;
      if (ft <= 0 && inc <= 0) return { cm: NaN, display: NaN };
      var totalIn = ft * 12 + inc;
      return { cm: cmFromIn(totalIn), display: totalIn };
    }
    var cmInp = this.querySelector('[data-input="height"]');
    var n = parseDecimal(cmInp ? cmInp.value : '');
    if (!isFinite(n) || n <= 0) return { cm: NaN, display: NaN };
    return { cm: n, display: n };
  };

  SmartSizeSuggestion.prototype.switchHeightMode = function (oldUnit, newUnit) {
    var cmRow = this.querySelector('[data-height-mode="cm"]');
    var imperialRow = this.querySelector('[data-height-mode="imperial"]');
    if (!cmRow || !imperialRow) return;
    var cmInput = cmRow.querySelector('[data-input="height"]');
    var ftInput = imperialRow.querySelector('[data-input="height_ft"]');
    var inInput = imperialRow.querySelector('[data-input="height_in"]');

    // Read current value as canonical cm
    var canonicalCm = NaN;
    if (oldUnit === 'cm') {
      var n = parseDecimal(cmInput.value);
      if (isFinite(n)) canonicalCm = n;
    } else {
      var ft = parseDecimal(ftInput.value); if (!isFinite(ft)) ft = 0;
      var inc = parseDecimal(inInput.value); if (!isFinite(inc)) inc = 0;
      if (ft > 0 || inc > 0) canonicalCm = cmFromIn(ft * 12 + inc);
    }

    // Show the destination row and populate
    if (newUnit === 'cm') {
      if (isFinite(canonicalCm)) cmInput.value = Math.round(canonicalCm * 10) / 10;
      cmRow.removeAttribute('hidden');
      imperialRow.setAttribute('hidden', '');
    } else {
      if (isFinite(canonicalCm)) {
        var ti = canonicalCm / 2.54;
        var ft2 = Math.floor(ti / 12);
        var in2 = Math.round((ti - ft2 * 12) * 10) / 10;
        if (in2 >= 12) { ft2 += 1; in2 -= 12; }
        ftInput.value = ft2;
        inInput.value = in2;
      }
      imperialRow.removeAttribute('hidden');
      cmRow.setAttribute('hidden', '');
    }
  };

  SmartSizeSuggestion.prototype.showPane = function (name) {
    if (this.applyNote) this.applyNote.setAttribute('hidden', '');
    this.formPane.toggleAttribute('hidden', name !== 'form');
    this.resultPane.toggleAttribute('hidden', name !== 'result');
    this.formPane.toggleAttribute('data-active', name === 'form');
    this.resultPane.toggleAttribute('data-active', name === 'result');
    if (name === 'result') this.refreshApplyMode();
    if (name === 'result' && this.heading) {
      var self = this;
      requestAnimationFrame(function () { self.heading.focus(); });
    }
  };

  SmartSizeSuggestion.prototype.refreshApplyMode = function () {
    var btn = this.querySelector('[data-action="apply"]');
    if (!btn) return;
    if (btn.dataset.applyLabel == null) btn.dataset.applyLabel = btn.innerHTML;
    this._applyMode = 'apply';
    var displayed = (this.resultSizeEl && this.resultSizeEl.textContent) || '';
    var found = displayed.match(/\[\s*([^\]\s]+)\s*\]/);
    var size = found ? found[1].trim() : (this._lastResult && this._lastResult.size ? String(this._lastResult.size).trim() : '');
    var match = null;
    if (size) {
      var sizeUpper = size.toUpperCase();
      document.querySelectorAll('size-picker [data-size-value]').forEach(function (row) {
        if (match) return;
        var v = (row.getAttribute('data-size-value') || '').trim();
        if (v && v.toUpperCase() === sizeUpper) match = row;
      });
    }
    if (match && match.classList.contains('is-unavailable')) {
      this._applyMode = 'remind';
      this._reminderTarget = match;
      var remindLabel = (this.remindBtn && this.remindBtn.textContent.trim()) || '[ Set a reminder ]';
      btn.textContent = remindLabel;
      if (this.applyNote && this.applyNoteText) {
        this.applyNoteText.textContent = String(this.config.unavailableMessage || '').replace('{size}', size);
        if (this.remindBtn) this.remindBtn.setAttribute('hidden', '');
        this.applyNote.removeAttribute('hidden');
      }
    } else {
      btn.innerHTML = btn.dataset.applyLabel;
    }
  };

  SmartSizeSuggestion.prototype.clearErrors = function () {
    this.querySelectorAll('.smart-size-modal__field-wrap.has-error').forEach(function (w) {
      w.classList.remove('has-error');
    });
    this.querySelectorAll('[data-error-for]').forEach(function (msg) {
      msg.setAttribute('hidden', '');
      msg.textContent = '';
    });
    this.querySelectorAll('[data-input]').forEach(function (inp) {
      inp.removeAttribute('aria-invalid');
    });
  };

  SmartSizeSuggestion.prototype.setError = function (inputName, message) {
    var inp = this.querySelector('[data-input="' + inputName + '"]');
    if (!inp) return;
    var wrap = inp.closest('.smart-size-modal__field-wrap');
    if (wrap) wrap.classList.add('has-error');
    var msg = this.querySelector('[data-error-for="' + inputName + '"]');
    if (msg) {
      msg.textContent = message;
      msg.removeAttribute('hidden');
    }
    inp.setAttribute('aria-invalid', 'true');
  };

  SmartSizeSuggestion.prototype.compute = function () {
    this.clearErrors();
    var weightInp = this.querySelector('[data-input="weight"]');
    var wRaw = parseDecimal(weightInp ? weightInp.value : '');
    var heightRead = this.readHeightCm();
    var heightCm = heightRead.cm;
    var hRaw = heightRead.display;

    if (!isFinite(heightCm) || heightCm <= 0) {
      this.setError('height', 'Please enter your height');
      return;
    }
    if (!isFinite(wRaw) || wRaw <= 0) {
      this.setError('weight', 'Please enter your weight');
      return;
    }

    var weightKg = toKg(wRaw, this.units.weight);

    if (heightCm < 120 || heightCm > 300) {
      this.setError('height', 'Please enter a height between ' + (this.units.length === 'imperial' ? "3'11\"–9'10\"" : '120–300 cm'));
      return;
    }
    if (weightKg < 30 || weightKg > 220) {
      this.setError('weight', 'Please enter a weight between ' + (this.units.weight === 'lb' ? '66–485 lb' : '30–220 kg'));
      return;
    }

    var matrixUnitH = this.config.matrixUnits && this.config.matrixUnits.height || 'cm';
    var matrixUnitW = this.config.matrixUnits && this.config.matrixUnits.weight || 'kg';
    var matrix = (this.config.matrix || []).map(function (r) {
      return {
        size: r.size,
        hMin: toCm(r.hMin, matrixUnitH),
        hMax: toCm(r.hMax, matrixUnitH),
        wMin: toKg(r.wMin, matrixUnitW),
        wMax: toKg(r.wMax, matrixUnitW)
      };
    });

    if (!matrix.length) {
      this.resultSizeEl.textContent = '[ — ]';
      this.resultExplanationEl.textContent = 'Size suggestions are being updated — please use the size guide.';
      this.secondaryWrap.setAttribute('hidden', '');
      this.showPane('result');
      return;
    }

    var base = findBaseSize(matrix, heightCm, weightKg);
    var secondary = base.candidates && base.candidates[1] ? base.candidates[1].size : null;

    // Optional measurements
    var optionalValues = {};
    var self = this;
    this.querySelectorAll('[data-input="optional"]').forEach(function (inp) {
      var raw = parseDecimal(inp.value);
      if (!isFinite(raw) || raw <= 0) return;
      var unit = inp.getAttribute('data-field-unit') || 'cm';
      var cm = toCm(raw, unit);
      optionalValues[inp.getAttribute('data-measurement-id')] = cm;
    });

    var bodyType = (this.querySelector('[data-input="body_type"]:checked') || {}).value || 'average';

    var votes = tallyOptionalVotes(this.config.optionalRanges, optionalValues);
    votes = applyBodyTypeVote(votes, matrix, base.size, bodyType);
    var resolved = reconcile(base.size, secondary, votes, matrix, bodyType);
    var explanation = buildExplanation(this.config.resultTemplate, resolved.size, resolved.explanationKey);

    // Render
    this.resultSizeEl.textContent = '[ ' + resolved.size + ' ]';
    this.resultExplanationEl.textContent = explanation;
    if (resolved.secondary && resolved.secondary !== resolved.size) {
      this.secondarySizeEl.textContent = resolved.secondary;
      this.secondaryWrap.removeAttribute('hidden');
    } else {
      this.secondaryWrap.setAttribute('hidden', '');
    }

    this._lastResult = { size: resolved.size, secondary: resolved.secondary || null };
    this._cachedLastSize = resolved.size;
    if (this.lastSizeEl) this.lastSizeEl.textContent = '[ ' + resolved.size + ' ]';

    if (this.config.persist) {
      this.persist(hRaw, wRaw, optionalValues, bodyType, resolved.size);
    }

    this.showPane('result');
  };

  SmartSizeSuggestion.prototype.persist = function (hRaw, wRaw, optionalValues, bodyType, recommendedSize) {
    var heightSnap = { unit: this.units.length };
    if (this.units.length === 'imperial') {
      var ftInp = this.querySelector('[data-input="height_ft"]');
      var inInp = this.querySelector('[data-input="height_in"]');
      heightSnap.ft = parseDecimal(ftInp ? ftInp.value : '');
      heightSnap.in = parseDecimal(inInp ? inInp.value : '');
    } else {
      var cmInp = this.querySelector('[data-input="height"]');
      heightSnap.cm = parseDecimal(cmInp ? cmInp.value : '');
    }
    var payload = {
      v: 1,
      units: this.units,
      heightSnap: heightSnap,
      weight: wRaw,
      optional: optionalValues || {},
      bodyType: bodyType || 'average'
    };
    safeSet(LS_KEY, JSON.stringify(payload));

    var handle = this.getProductHandle();
    if (handle && recommendedSize) {
      safeSet(LS_RESULT_PREFIX + handle, JSON.stringify({ size: recommendedSize, ts: Date.now() }));
    }
    if (recommendedSize) {
      safeSet(LS_LAST_KEY, JSON.stringify({ size: recommendedSize, handle: handle || null, ts: Date.now() }));
    }
  };

  SmartSizeSuggestion.prototype.hydrateFromStorage = function () {
    var raw = safeGet(LS_KEY);
    if (!raw) return;
    try {
      var data = JSON.parse(raw);
      if (!data || data.v !== 1) return;

      // Restore unit toggles
      if (data.units && data.units.length) {
        var lengthBtn = this.querySelector('[data-unit-group="length"][data-unit-value="' + data.units.length + '"]');
        if (lengthBtn && !lengthBtn.classList.contains('is-active')) lengthBtn.click();
      }
      if (data.units && data.units.weight) {
        var weightBtn = this.querySelector('[data-unit-group="weight"][data-unit-value="' + data.units.weight + '"]');
        if (weightBtn && !weightBtn.classList.contains('is-active')) weightBtn.click();
      }

      // Restore values
      var weightInp = this.querySelector('[data-input="weight"]');
      if (weightInp && isFinite(data.weight)) weightInp.value = data.weight;

      if (data.heightSnap) {
        if (data.heightSnap.unit === 'imperial') {
          var ftInp = this.querySelector('[data-input="height_ft"]');
          var inInp = this.querySelector('[data-input="height_in"]');
          if (ftInp && isFinite(data.heightSnap.ft)) ftInp.value = data.heightSnap.ft;
          if (inInp && isFinite(data.heightSnap.in)) inInp.value = data.heightSnap.in;
        } else {
          var cmInp = this.querySelector('[data-input="height"]');
          if (cmInp && isFinite(data.heightSnap.cm)) cmInp.value = data.heightSnap.cm;
        }
      } else if (isFinite(data.height)) {
        // back-compat: older payload with a single numeric height
        var heightInp = this.querySelector('[data-input="height"]');
        if (heightInp) heightInp.value = data.height;
      }

      if (data.optional) {
        var self = this;
        Object.keys(data.optional).forEach(function (id) {
          var inp = self.querySelector('[data-input="optional"][data-measurement-id="' + id + '"]');
          if (inp) {
            var fieldUnit = inp.getAttribute('data-field-unit') || 'cm';
            var stored = data.optional[id]; // canonical cm
            inp.value = fieldUnit === 'in' ? Math.round(inFromCm(stored) * 10) / 10 : Math.round(stored * 10) / 10;
          }
        });
      }

      if (data.bodyType) {
        var btInput = this.querySelector('[data-input="body_type"][value="' + data.bodyType + '"]');
        if (btInput) btInput.checked = true;
      }
    } catch (e) {}
  };

  SmartSizeSuggestion.prototype.refreshLastBanner = function () {
    if (!this.lastBanner) return;
    var handle = this.getProductHandle();
    var lastSize = null;

    if (handle) {
      var raw = safeGet(LS_RESULT_PREFIX + handle);
      if (raw) {
        try {
          var data = JSON.parse(raw);
          if (data && data.size) lastSize = data.size;
        } catch (e) {}
      }
    }

    if (!lastSize) {
      var lastRaw = safeGet(LS_LAST_KEY);
      if (lastRaw) {
        try {
          var last = JSON.parse(lastRaw);
          if (last && last.size) lastSize = last.size;
        } catch (e) {}
      }
    }

    if (lastSize) {
      this.lastSizeEl.textContent = '[ ' + lastSize + ' ]';
      this.lastBanner.removeAttribute('hidden');
      this._cachedLastSize = lastSize;
    } else {
      this.lastBanner.setAttribute('hidden', '');
      this._cachedLastSize = null;
    }
  };

  SmartSizeSuggestion.prototype.useLastResult = function () {
    if (!this._cachedLastSize) return;
    this.resultSizeEl.textContent = '[ ' + this._cachedLastSize + ' ]';
    this.resultExplanationEl.textContent = "Based on your previous measurements, this is your recommended fit.";
    this.secondaryWrap.setAttribute('hidden', '');
    this._lastResult = { size: this._cachedLastSize, secondary: null };
    this.showPane('result');
  };

  SmartSizeSuggestion.prototype.useBoughtSize = function (btn) {
    var size = btn && btn.getAttribute('data-size');
    if (!size) return;
    this.resultSizeEl.textContent = '[ ' + size + ' ]';
    this.resultExplanationEl.textContent = btn.getAttribute('data-explanation') || '';
    if (this.secondaryWrap) this.secondaryWrap.setAttribute('hidden', '');
    this._lastResult = { size: size, secondary: null };
    this.showPane('result');
  };

  SmartSizeSuggestion.prototype.resetForm = function () {
    this.clearErrors();
    var handle = this.getProductHandle();
    if (handle) safeRemove(LS_RESULT_PREFIX + handle);
    safeRemove(LS_LAST_KEY);
    this.lastBanner.setAttribute('hidden', '');
    this._cachedLastSize = null;
    this._lastResult = null;
    this.showPane('form');
  };

  SmartSizeSuggestion.prototype.applySize = function () {
    // Always read the size from the rendered result pane to avoid any stale reference
    var displayed = (this.resultSizeEl && this.resultSizeEl.textContent) || '';
    var match = displayed.match(/\[\s*([^\]\s]+)\s*\]/);
    var size = match ? match[1].trim() : (this._lastResult && this._lastResult.size ? String(this._lastResult.size).trim() : '');
    if (!size) { this.close(); return; }

    // Sync to local storage
    var handle = this.getProductHandle();
    if (handle) {
      safeSet(LS_RESULT_PREFIX + handle, JSON.stringify({ size: size, ts: Date.now() }));
    }
    safeSet(LS_LAST_KEY, JSON.stringify({ size: size, handle: handle || null, ts: Date.now() }));

    var outcome = this.selectVariantSize(size);
    if (outcome === 'applied') {
      this.close();
      return;
    }
    this.showApplyNote(outcome, size);
  };

  SmartSizeSuggestion.prototype.showApplyNote = function (outcome, size) {
    if (!this.applyNote || !this.applyNoteText) { this.close(); return; }
    var tpl = outcome === 'unavailable' ? this.config.unavailableMessage : this.config.missingMessage;
    this.applyNoteText.textContent = String(tpl || '').replace('{size}', size);
    if (this.remindBtn) this.remindBtn.toggleAttribute('hidden', !(outcome === 'unavailable' && this._reminderTarget));
    this.applyNote.removeAttribute('hidden');
  };

  SmartSizeSuggestion.prototype.remindSize = function () {
    var target = this._reminderTarget;
    this.close();
    if (target) target.click();
  };

  SmartSizeSuggestion.prototype.selectVariantSize = function (size) {
    var sizeUpper = size.toUpperCase();
    var clicked = false;
    var unavailable = false;
    this._reminderTarget = null;

    var pickerMatch = null;
    document.querySelectorAll('size-picker [data-size-value]').forEach(function (btn) {
      if (pickerMatch) return;
      var v = (btn.getAttribute('data-size-value') || '').trim();
      if (v && v.toUpperCase() === sizeUpper) pickerMatch = btn;
    });
    if (pickerMatch) {
      if (pickerMatch.classList.contains('is-unavailable')) {
        this._reminderTarget = pickerMatch;
        return 'unavailable';
      }
      pickerMatch.click();
      return 'applied';
    }
    var selects = document.querySelectorAll('product-variant-selects, variant-selects, variant-radios');

    selects.forEach(function (host) {
      if (clicked) return;
      // Try input[type=radio] matching the size value
      var inputs = host.querySelectorAll('input[type="radio"], button[role="radio"], [data-option-value]');
      inputs.forEach(function (el) {
        if (clicked) return;
        var v = (el.value || el.getAttribute('data-option-value') || el.textContent || '').trim();
        if (!v) return;
        if (v.toUpperCase() === sizeUpper) {
          if (el.disabled) { unavailable = true; return; }
          if (el.tagName === 'INPUT') {
            el.checked = true;
            el.dispatchEvent(new Event('change', { bubbles: true }));
          } else if (typeof el.click === 'function') {
            el.click();
          }
          clicked = true;
        }
      });

      // <select> fallback
      if (!clicked && !unavailable) {
        var sels = host.querySelectorAll('select:not([data-size-picker-select])');
        sels.forEach(function (s) {
          if (clicked || unavailable) return;
          for (var i = 0; i < s.options.length; i++) {
            if ((s.options[i].value || '').trim().toUpperCase() === sizeUpper ||
                (s.options[i].text || '').trim().toUpperCase() === sizeUpper) {
              if (s.options[i].disabled) { unavailable = true; break; }
              s.selectedIndex = i;
              s.dispatchEvent(new Event('change', { bubbles: true }));
              clicked = true;
              break;
            }
          }
        });
      }
    });

    // 2. Fallback: any label that contains the size text inside .product__variant-options
    if (!clicked && !unavailable) {
      var labels = document.querySelectorAll('.product__variant-options label, .product__variant-options input');
      labels.forEach(function (el) {
        if (clicked) return;
        var txt = (el.value || el.textContent || '').trim();
        if (txt.toUpperCase() === sizeUpper && typeof el.click === 'function') {
          el.click();
          clicked = true;
        }
      });
    }
    return clicked ? 'applied' : (unavailable ? 'unavailable' : 'missing');
  };

  SmartSizeSuggestion.prototype.getProductHandle = function () {
    // Try common Shopify hooks
    var dataEl = document.querySelector('[data-product-handle]');
    if (dataEl) return dataEl.getAttribute('data-product-handle');
    var meta = document.querySelector('meta[property="product:handle"], meta[name="product:handle"]');
    if (meta) return meta.getAttribute('content');
    // URL fallback /products/<handle>
    var m = window.location.pathname.match(/\/products\/([^/?#]+)/);
    return m ? m[1] : null;
  };

  if (typeof window.customElements !== 'undefined') {
    window.customElements.define('smart-size-suggestion', SmartSizeSuggestion);
  }
})();
